import { Component, computed, inject, input, output } from "@angular/core";

import { LanguageService } from "../../../services";

export type PeriodRailStep = {
  id: number;
  label: string;
  year: number;
};

type PeriodRailGroup = {
  year: number;
  span: number;
  startIndex: number;
  ratio: number;
  issue: string | undefined;
};

@Component({
  selector: "adaa-period-rail",
  templateUrl: "./period-rail.html",
  styleUrl: "./period-rail.scss",
})
export class PeriodRailComponent {
  readonly languageService = inject(LanguageService);

  steps = input.required<PeriodRailStep[]>();
  selectedId = input<number | undefined>(undefined);
  activeYear = input<number | undefined>(undefined);
  yearsSelectable = input<boolean>(false);
  emphasizeYears = input<boolean>(false);
  yearIssues = input<Record<number, string>>({});
  truncateStart = input<boolean>(false);
  truncateEnd = input<boolean>(false);

  stepSelect = output<number>();
  yearSelect = output<number>();

  railSteps = computed(() => {
    const steps = this.steps();
    return steps.map((step, index) => ({ ...step, ratio: (index + 0.5) / steps.length }));
  });

  /**
   * @description
   *  Consecutive steps sharing a year become one band cell spanning them. The band is a
   *  group header, never a step, so a year that holds a single step (annual and
   *  multi-year plans) collapses it and lets the step label carry the year instead.
   */
  groups = computed<PeriodRailGroup[]>(() => {
    const issues = this.yearIssues();
    const steps = this.steps();
    const groups: Omit<PeriodRailGroup, "ratio">[] = [];

    steps.forEach(({ year }, index) => {
      const current = groups.at(-1);
      if (current?.year === year) {
        current.span += 1;
        return;
      }
      groups.push({ year, span: 1, startIndex: index, issue: issues[year] });
    });

    return groups.map((group) => ({ ...group, ratio: (group.startIndex + group.span / 2) / steps.length }));
  });

  /**
   * @description
   *  The band collapses only when the step *is* the year — annual and multi-year plans,
   *  where the label already carries it. Any shorter step keeps the band, even a window
   *  that holds a single step per year.
   */
  showBand = computed(() => this.steps().some((step) => step.label !== String(step.year)));

  selectedIndex = computed(() => this.railSteps().findIndex(({ id }) => id === this.selectedId()));
  selectedStep = computed(() => this.railSteps()[this.selectedIndex()]);

  /**
   * @description
   *  A whole year can be the selection instead of a step, in which case no tick is active
   *  and the band cell carries the selection on its own.
   */
  isYearSelected = computed(() => this.selectedGroup() !== undefined);
  selectedGroup = computed(() => this.groups().find(({ year }) => year === this.activeYear()));
  selectedYear = computed(() => this.selectedGroup()?.year ?? this.selectedStep()?.year);
  selectedRatio = computed(() => this.selectedStep()?.ratio ?? 0);

  /**
   * @description
   *  The pill names a quarter alone. A year selection is already carried by the band cell
   *  it highlights, or by the step label when the step *is* the year, so a pill over it
   *  would only repeat what the rail already reads.
   */
  showPill = computed(() => {
    if (this.isYearSelected()) return false;

    const step = this.selectedStep();
    if (!step) return false;
    return step.label !== String(step.year);
  });

  pill = computed(() => {
    const step = this.selectedStep();
    if (!step) return "";
    return `${step.label} ${step.year}`;
  });

  public selectYear(year: number): void {
    if (!this.yearsSelectable()) return;
    this.yearSelect.emit(year);
  }
}
